import React, { useMemo } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { getStartOf, monthIdString } from '../../../util/dates';
// Code config (not hosted-configurable): the Sharetribe time-slots query horizon.
import { dayCountAvailableForBooking } from '../../../config/configStripe';
import DatePicker from '../../../components/DatePicker/DatePickers/DatePicker';
import { getISODateString } from '../../../components/DatePicker/DatePickers/DatePicker.helpers';

import {
  flattenMonthlyTimeSlots,
  slotsWithinDays,
  upcomingSlotDays,
  classBookableFromMs,
  slotSeats,
  listingTimeZone,
} from './classDetailData';

import css from './ClassDetail.module.css';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const timeZoneMaybe = timeZone => (timeZone ? { timeZone } : {});

/** The calendar-grid Date for a 'YYYY-MM-DD' day key (grid dates are plain local calendar days). */
const dateFromDayKey = dayKey => {
  const [y, m, d] = dayKey.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/**
 * ClassSessionPicker -- the ONE single-session date + time picker, rendered identically by the
 * on-page "Schedule & availability" card and the Activity Booking modal so the two can never
 * disagree. A month calendar (any future date the provider has scheduled, up to the time-slots
 * query horizon; stepping into an unfetched month lazily fetches its availability) over the
 * selected day's time slots with true seats-remaining counts.
 *
 * Controlled: day/slot selection lives in the caller. Slot chips are buttons when `onSelectSlot`
 * is given (parents booking); otherwise plain chips (provider/owner preview stays display-only).
 *
 * @param {Object} props
 * @param {Object} props.listing
 * @param {Object} props.monthlyTimeSlots the ListingPage duck's monthlyTimeSlots
 * @param {Object} props.intl
 * @param {string|null} props.selectedDayKey selected 'YYYY-MM-DD' (falls back to the first day)
 * @param {string|null} [props.selectedSlotKey] selected slot key (start ISO string)
 * @param {Function} props.onSelectDay (dayKey) => void
 * @param {Function} [props.onSelectSlot] (slotKey, dayKey) => void; omit for display-only chips
 * @param {Function} [props.onFetchTimeSlots] (listingId, start, end, tz, options) => void
 */
const ClassSessionPicker = props => {
  const {
    listing,
    monthlyTimeSlots,
    intl,
    selectedDayKey,
    selectedSlotKey = null,
    onSelectDay,
    onSelectSlot,
    onFetchTimeSlots,
    // 'stacked' (default: calendar above slots -- the modals and provider hosts) or 'columns'
    // (class page: times left, calendar right; adds a next-days quick-switch row).
    layout = 'stacked',
  } = props;

  const timeZone = listingTimeZone(listing);
  // Advance-booking cap: never offer a slot beyond the horizon, so nothing shown is later rejected by
  // the checkout. The month-fetch below already skips whole months past the horizon; this also trims
  // the days between the cap and that month's end.
  const bookableFromMs = classBookableFromMs(listing);
  const slots = slotsWithinDays(
    flattenMonthlyTimeSlots(monthlyTimeSlots, new Date(), bookableFromMs),
    dayCountAvailableForBooking
  );
  const days = upcomingSlotDays(slots, { maxDays: Infinity, timeZone });
  // A drop-in with a future start date has no bookable sessions yet -- say "Bookable from {date}"
  // rather than the bare "no upcoming sessions", so a not-yet-started class reads clearly.
  const notYetStarted = bookableFromMs != null && bookableFromMs > Date.now();
  const selectedDay = days.find(d => d.dayKey === selectedDayKey) || days[0] || null;
  const bookableDayKeys = new Set(days.map(d => d.dayKey));
  const selectedDayKeyEffective = selectedDay?.dayKey || null;
  // STABLE identity per day: the DatePicker syncs its internal state off `value`, so handing it a
  // freshly-constructed Date every render would re-sync it on every fetch-driven re-render and
  // interrupt month navigation mid-slide.
  const selectedDateValue = useMemo(
    () => (selectedDayKeyEffective ? dateFromDayKey(selectedDayKeyEffective) : null),
    [selectedDayKeyEffective]
  );

  // Lazily fetch a month's availability when the calendar steps into it. The grid's first-of-month
  // is a plain local date; anchoring on mid-month before taking the listing-timezone month start
  // keeps the fetch on the intended month whatever the viewer's timezone. Months already fetched
  // (the key exists, even when empty) and months beyond the query horizon are skipped; the next
  // month is prefetched so the grid's tail days are never stale.
  const ensureMonthFetched = firstOfMonth => {
    if (!onFetchTimeSlots) {
      return;
    }
    const midMonth = new Date(firstOfMonth.getFullYear(), firstOfMonth.getMonth(), 15);
    const horizon = new Date(Date.now() + dayCountAvailableForBooking * MS_PER_DAY);
    [0, 1].forEach(offset => {
      const start = getStartOf(midMonth, 'month', timeZone, offset, 'months');
      if (start > horizon) {
        return;
      }
      const monthId = monthIdString(start, timeZone);
      if (!monthlyTimeSlots?.[monthId]) {
        const end = getStartOf(start, 'month', timeZone, 1, 'months');
        onFetchTimeSlots(listing.id, start, end, timeZone, null);
      }
    });
  };

  // "Loading" covers both an in-flight month fetch and the moment before the host's initial
  // fetches land -- the empty message must never flash while availability is simply on its way.
  // A draft or pending class has no bookable sessions to fetch yet: say so instead of "Loading".
  const isUnpublished = !!listing?.attributes?.state && listing.attributes.state !== 'published';
  const monthDataLoading =
    !isUnpublished &&
    (Object.keys(monthlyTimeSlots || {}).length === 0 ||
      Object.values(monthlyTimeSlots || {}).some(m => m?.fetchTimeSlotsInProgress));

  const isColumns = layout === 'columns';

  // Columns mode: the next few bookable days as quick-switch chips, so the times column always has
  // an obvious "pick a day" affordance without touching the calendar.
  const upcomingDayChips =
    isColumns && days.length > 0 ? (
      <div className={css.dayChipRow}>
        {days.slice(0, 5).map(d => (
          <button
            key={d.dayKey}
            type="button"
            className={classNames(css.dayChip, {
              [css.dayChipSelected]: d.dayKey === selectedDayKeyEffective,
            })}
            aria-pressed={d.dayKey === selectedDayKeyEffective}
            onClick={() => onSelectDay(d.dayKey)}
          >
            {intl.formatDate(d.date, { weekday: 'short', month: 'short', day: 'numeric', ...timeZoneMaybe(timeZone) })}
          </button>
        ))}
      </div>
    ) : null;

  const calendarBlock = (
    <div className={css.bookingCalendar}>
        <DatePicker
          range={false}
          showMonthStepper={true}
          hasFocusOnMount={false}
          startDate={selectedDayKeyEffective || getISODateString(new Date())}
          value={selectedDateValue}
          isDayBlocked={day => !bookableDayKeys.has(getISODateString(day))}
          onChange={date => {
            const key = getISODateString(date);
            if (bookableDayKeys.has(key)) {
              onSelectDay(key);
            }
          }}
          onMonthChange={ensureMonthFetched}
        />
        {monthDataLoading ? (
          <p className={css.calendarLoading}>
            <FormattedMessage id="ClassDetailPage.loadingAvailability" />
          </p>
        ) : null}
      </div>
  );

  const slotsBlock = (
    <>
      {upcomingDayChips}

      {days.length === 0 && !monthDataLoading ? (
        <p className={css.scheduleEmpty}>
          {isUnpublished ? (
            <FormattedMessage id="ClassDetailPage.sessionsAfterPublish" />
          ) : notYetStarted ? (
            <FormattedMessage
              id="ClassDetailPage.bookableFrom"
              values={{
                date: intl.formatDate(new Date(bookableFromMs), {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }),
              }}
            />
          ) : (
            <FormattedMessage id="ClassDetailPage.noUpcomingSessions" />
          )}
        </p>
      ) : null}

      {selectedDay ? (
        <>
          <p className={css.slotRowLabel}>
            <FormattedMessage
              id="ClassDetailPage.timeSlotsFor"
              values={{
                day: intl.formatDate(selectedDay.date, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  ...timeZoneMaybe(timeZone),
                }),
              }}
            />
          </p>
          <div className={css.slotChipRow}>
            {selectedDay.slots.map(slot => {
              const seats = slotSeats(slot);
              const key = slot.attributes.start.toISOString();
              // The availability API normally omits sold-out slots; the disable is defensive.
              const soldOut = seats != null && seats <= 0;
              const seatsMaybe =
                seats != null ? (
                  <span className={css.slotSeats}>
                    <FormattedMessage id="ClassDetailPage.slotSpotsLeft" values={{ count: seats }} />
                  </span>
                ) : null;
              return onSelectSlot ? (
                <button
                  key={key}
                  type="button"
                  disabled={soldOut}
                  className={classNames(css.slotChipSelectable, {
                    [css.slotChipSelected]: key === selectedSlotKey,
                  })}
                  aria-pressed={key === selectedSlotKey}
                  onClick={() => onSelectSlot(key, selectedDay.dayKey)}
                >
                  {intl.formatTime(slot.attributes.start, timeZoneMaybe(timeZone))}
                  {seatsMaybe}
                </button>
              ) : (
                <span key={key} className={css.slotChip}>
                  {intl.formatTime(slot.attributes.start, timeZoneMaybe(timeZone))}
                  {seatsMaybe}
                </span>
              );
            })}
          </div>
        </>
      ) : null}
    </>
  );

  return isColumns ? (
    <div className={css.pickerColumns}>
      <div className={css.pickerSlotsColumn}>{slotsBlock}</div>
      <div className={css.pickerCalendarColumn}>{calendarBlock}</div>
    </div>
  ) : (
    <>
      {calendarBlock}
      {slotsBlock}
    </>
  );
};

export default ClassSessionPicker;
